// src/pages/screens/DatabaseListScreen.js
import { useEffect, useState } from "react";
import { useDatabase } from "../databaseContext";
import { useNavigate, Link } from "react-router-dom";
import { Select, Tag, message, Dropdown, Menu, Button, Modal, Input, Spin, Table } from "antd";
import {
  MoreOutlined,
  EyeOutlined,
  UserOutlined,
  TableOutlined,
  SafetyOutlined,
  DatabaseOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { format } from "date-fns"; // Or your own formatting
import axios from "axios";
import { AUTH_TOKEN, REACT_APP_API, ORG_ID, DOMAIN_ID } from "../../../constants";

const DatabaseListScreen = ({ showNotification, onTabChange, onDatabaseSelect }) => {
  const navigate = useNavigate();
  const { dbNames, fetchDbNames, dbList, setDbList, fetchDbList } = useDatabase();

  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedHost, setSelectedHost] = useState(null);
  const [hostSearchTimeout, setHostSearchTimeout] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // For user messages
  const [notification, contextHolder] = message.useMessage();

  // Fetch unique database names on mount
  useEffect(() => {
    fetchDbNames();
  }, [fetchDbNames]);

  // Handle host filter from VMScreen
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hostFilter = params.get("host");
    if (hostFilter) {
      setSelectedHost(hostFilter);
    }
  }, []);

  // Actually fetch the DB list whenever filters/page change
  useEffect(() => {
    loadDatabaseData();
  }, [selectedDatabase, selectedStatus, selectedHost, currentPage, pageSize]);

  const loadDatabaseData = async () => {
    setIsLoading(true);
    const filters = [];
    if (selectedDatabase) {
      filters.push({ filterParameter: "Database", filterValue: selectedDatabase });
    }
    if (selectedStatus) {
      filters.push({ filterParameter: "Status", filterValue: selectedStatus });
    }
    if (selectedHost) {
      filters.push({ filterParameter: "Host", filterValue: selectedHost });
    }

    try {
      const { data, totalCount: tc } = await fetchDbList(filters, pageSize, currentPage + 1);
      setDbList(data);
      setTotalCount(tc);
    } catch (error) {
      notification.error({ content: "Failed to fetch database list" });
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch tables for a database
  const fetchTableNames = async (dbName, host) => {
    try {
      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        dbName: dbName,
        host: host,
      };

      const res = await axios.post(`${REACT_APP_API}/api/v1/databaseService/listTables`, payload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      });

      if (res.data?.data) {
        // Convert the response data into an array of table names
        return Object.keys(res.data.data);
      }
      return [];
    } catch (error) {
      notification.error({ message: "Error fetching table names", description: error.message });
      return [];
    }
  };

  // Function to fetch table fields
  const fetchTableFields = async (dbName, tables) => {
    try {
      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        tenantName: dbName,
        tableNames: tables,
      };

      const res = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/getTableFields`,
        payload,
        {
          headers: {
            withCredentials: true,
            "X-Authorization": AUTH_TOKEN(),
          },
        }
      );

      if (res.data?.fields) {
        return res.data.fields;
      }
      return {};
    } catch (error) {
      notification.error({ message: "Error fetching table fields", description: error.message });
      return {};
    }
  };

  const handleMenuClick = async (db, action) => {
    switch (action) {
      case "viewUsers":
        // Example logic
        navigate("/pam/databases/users", { state: { databaseFilter: db.db_name } });
        break;
      case "createPolicy":
        notification.loading({ content: "Preparing policy creation...", key: "policyCreation" });

        // Generate a dynamic policy name
        const prefix = "db-";
        const characters = "0123456789";
        let policyName = prefix;
        const sections = [7, 8];

        for (let sectionLength of sections) {
          for (let i = 0; i < sectionLength; i++) {
            const randomCharIndex = Math.floor(Math.random() * characters.length);
            policyName += characters.charAt(randomCharIndex);
          }
          if (sectionLength !== 8) {
            policyName += "-";
          }
        }

        // Fetch tables for this database
        const tables = await fetchTableNames(db.db_name, db.host);

        // Create initial field masking object (empty arrays)
        const initialFieldMasking = {};
        tables.forEach((table) => {
          initialFieldMasking[table] = [];
        });

        // Get a subset of tables (up to 5) to preselect by default
        const preselectedTables = [];

        // Fetch fields for the selected tables
        const fields = await fetchTableFields(db.db_name, preselectedTables);

        // Create the policy draft
        const policyDraft = {
          policyType: "database",
          policyName: policyName,
          policyJson: {
            policyName: policyName,
            policyType: "database",
            database: {
              database_name: db.db_name,
              host: db.host,
              tables: preselectedTables,
              field_masking: initialFieldMasking,
            },
          },
          // Store the fields data to be used when the form loads
          fieldsData: fields,
        };

        // Save to localStorage
        localStorage.setItem("policyDraft.basicPolicyInformation", JSON.stringify(policyDraft));

        console.log("Policy draft saved:", policyDraft);
        notification.success({ content: "Ready to create policy", key: "policyCreation" });
        navigate("/pam/policy/v2/createPolicy");
        break;
      case "viewTables":
        onDatabaseSelect(db.db_name);
        onTabChange("tables", { host: db.host, database: db.db_name });
        break;
      case "viewDetails":
        Modal.info({
          title: "Database Details",
          width: 700,
          className: "database-details-modal",
          okText: "Close",
          content: (
            <div className="space-y-6 p-4">
              <div className="rounded-lg border border-gray-100">
                <table className="min-w-full divide-y divide-gray-200">
                  <tbody className="divide-y divide-gray-100">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">Database Name</p>
                        <p className="font-medium mt-1">{db.db_name}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">Type</p>
                        <p className="font-medium mt-1">{db.db_type}</p>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">Host</p>
                        <p className="font-medium mt-1 truncate" title={db.host}>
                          {db.host}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">Port</p>
                        <p className="font-medium mt-1">{db.port}</p>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">Status</p>
                        <div className="mt-1">
                          <Tag color={db.status === "Active" ? "green" : "red"}>{db.status}</Tag>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-500">Created At</p>
                        <p className="font-medium mt-1">
                          {format(new Date(db.created_at), "yyyy-MM-dd HH:mm:ss")}
                        </p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h4 className="font-medium text-base mb-4">Quick Actions</h4>
                <div className="flex gap-3">
                  <Button
                    icon={<UserOutlined />}
                    onClick={() => {
                      Modal.destroyAll();
                      handleMenuClick(db, "viewUsers");
                    }}
                  >
                    View Users
                  </Button>
                  <Button
                    icon={<SafetyOutlined />}
                    onClick={() => {
                      Modal.destroyAll();
                      handleMenuClick(db, "createPolicy");
                    }}
                  >
                    Grant Access
                  </Button>
                  <Button
                    icon={<TableOutlined />}
                    onClick={() => {
                      Modal.destroyAll();
                      handleMenuClick(db, "viewTables");
                    }}
                  >
                    View Tables
                  </Button>
                </div>
              </div>
            </div>
          ),
        });
        break;
      default:
        break;
    }
  };

  const columns = [
    {
      title: "Database Name",
      dataIndex: "db_name",
      key: "db_name",
      render: (text, record) => (
        <Button type="link" onClick={() => handleMenuClick(record, "viewDetails")}>
          {text}
        </Button>
      ),
    },
    {
      title: "Type",
      dataIndex: "db_type",
      key: "db_type",
      render: (text) => <Tag color="geekblue">{text}</Tag>,
    },
    { title: "Host", dataIndex: "host", key: "host" },
    { title: "Port", dataIndex: "port", key: "port" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => <Tag color={text === "Active" ? "green" : "red"}>{text}</Tag>,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => format(new Date(text), "yyyy-MM-dd HH:mm:ss"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="viewDetails" onClick={() => handleMenuClick(record, "viewDetails")}>
                <EyeOutlined /> View Details
              </Menu.Item>
              <Menu.Item key="viewUsers" onClick={() => handleMenuClick(record, "viewUsers")}>
                <UserOutlined /> View Users
              </Menu.Item>
              <Menu.Item key="viewTables" onClick={() => handleMenuClick(record, "viewTables")}>
                <TableOutlined /> View Tables
              </Menu.Item>
              <Menu.Item key="createPolicy" onClick={() => handleMenuClick(record, "createPolicy")}>
                <SafetyOutlined /> Grant Access
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  // Combine filters to display as tags
  const combinedFilters = [];
  if (selectedDatabase) {
    combinedFilters.push({ key: "Database", display: `Database: ${selectedDatabase}` });
  }
  if (selectedStatus) {
    combinedFilters.push({ key: "Status", display: `Status: ${selectedStatus}` });
  }
  if (selectedHost) {
    combinedFilters.push({ key: "Host", display: `Host: ${selectedHost}` });
  }

  return (
    <div className="p-6">
      {contextHolder}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Database Management</h2>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Database Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Database</label>
            <Select
              showSearch
              allowClear
              placeholder="Select Database"
              style={{ width: "100%" }}
              value={selectedDatabase}
              onChange={(value) => {
                setSelectedDatabase(value);
                setCurrentPage(0);
              }}
              filterOption={(input, option) =>
                (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
              }
            >
              {dbNames.map((db) => {
                // Extract database name from the string format "dbname (host)"
                const dbName = db.split(" (")[0];
                return (
                  <Select.Option key={dbName} value={dbName}>
                    {dbName}
                  </Select.Option>
                );
              })}
            </Select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <Select
              placeholder="Select Status"
              style={{ width: "100%" }}
              value={selectedStatus}
              onChange={(value) => {
                setSelectedStatus(value);
                setCurrentPage(0);
              }}
              allowClear
            >
              <Select.Option value="Active">Active</Select.Option>
              <Select.Option value="Inactive">Inactive</Select.Option>
            </Select>
          </div>

          {/* Host Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Host</label>
            <Input
              allowClear
              placeholder="Enter Host (e.g. 52.142)"
              value={selectedHost}
              onChange={(e) => {
                const value = e.target.value;
                if (hostSearchTimeout) {
                  clearTimeout(hostSearchTimeout);
                }
                setHostSearchTimeout(
                  setTimeout(() => {
                    setSelectedHost(value);
                    setCurrentPage(0);
                  }, 500)
                );
              }}
            />
          </div>

          <div className="flex items-end">
            <Button type="primary" onClick={loadDatabaseData} icon={<SearchOutlined />}>
              Search
            </Button>
          </div>
        </div>
      </div>

      {/* Applied Filters */}
      {combinedFilters.length > 0 && (
        <div className="mb-4">
          <p className="text-sm text-gray-700 mb-2">Applied Filters</p>
          {combinedFilters.map(({ key, display }) => (
            <Tag
              key={key}
              closable
              onClose={() => {
                if (key === "Database") setSelectedDatabase(null);
                if (key === "Status") setSelectedStatus(null);
                if (key === "Host") setSelectedHost(null);
                setCurrentPage(0);
              }}
              style={{ marginBottom: 8, marginRight: 8 }}
            >
              {display}
            </Tag>
          ))}
        </div>
      )}

      {/* Database Table */}
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="py-12 flex justify-center">
            <Spin size="large" />
          </div>
        ) : dbList.length === 0 ? (
          <div className="py-12 flex flex-col items-center justify-center text-gray-500">
            <DatabaseOutlined className="text-4xl mb-4" />
            <p>No databases found</p>
            <p className="text-sm mt-2">
              Try adjusting your filters or{" "}
              <Link to="/pam/databases/install" className="text-blue-500 hover:underline">
                install a database agent
              </Link>
            </p>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={dbList}
            pagination={{
              current: currentPage + 1,
              pageSize,
              total: totalCount,
              onChange: (page) => setCurrentPage(page - 1),
              showSizeChanger: true,
              showQuickJumper: true,
              onShowSizeChange: (_, newSize) => setPageSize(newSize),
            }}
            rowKey={(record) => record.id}
          />
        )}
      </div>
    </div>
  );
};

export default DatabaseListScreen;
